import { useState } from 'react'
import { Controller, Control } from 'react-hook-form'
import { FormControl } from '@mui/material_modern'
import { AdapterDateFns } from '@mui/x-date-pickers_modern/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers_modern/LocalizationProvider'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers_modern'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { getDateFormatPatternFromLocale } from 'utils/helpers'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import dayjs from 'dayjs'

interface DatePickerV2Props {
  name: string
  label: string
  control: Control<any>
  variant: 'standard' | 'filled' | 'outlined' | undefined
  defaultDate: Date
  datePickerProps: Record<string, any>
  [key: string]: any
}

const DatePickerV2: React.FC<DatePickerV2Props> = ({
  name,
  label,
  control,
  variant,
  defaultDate,
  datePickerProps,
  ...restProps
}) => {
  const [open, setOpen] = useState(false)
  const { locale } = useAppSelector(state => state.auth)
  const { dateFormat, muiDateFormat } = getDateFormatPatternFromLocale(locale)

  const formatDate = (value: string | Date) => {
    const dateValue = new Date(value).getTime()
    const inputValue = !isNaN(dateValue) && dayjs(dateValue).isValid() ? dateFormatter(dateValue) : value

    return inputValue
  }

  return (
    <FormControl sx={{ width: '100%' }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Controller
          render={({ field: { onChange, value } }) => {
            const disabled = datePickerProps.disabled || restProps.disabled
            const onOpen = () => {
              if (disabled) return
              setOpen(true)
            }
            return (
              <MuiDatePicker
                open={open}
                onOpen={onOpen}
                onClose={() => setOpen(false)}
                label={label}
                value={value}
                onChange={onChange}
                {...datePickerProps}
                slotProps={{
                  textField: {
                    variant,
                    ...restProps,
                    sx: {
                      ...(restProps.sx || {}),
                      '& > div': {
                        cursor: disabled ? 'unset' : 'pointer',
                      },
                      input: {
                        ...(restProps.sx?.input || {}),
                        cursor: disabled ? 'unset' : 'pointer',
                      },
                    },
                    onKeyDown: event => {
                      if (event.key === 'Tab') return
                      event.preventDefault()
                    },
                    onClick: onOpen,
                    inputProps: {
                      value: formatDate(value),
                      format: muiDateFormat,
                      placeholder: dateFormat,
                    },
                  },
                }}
              />
            )
          }}
          name={name}
          control={control}
          defaultValue={defaultDate}
        />
      </LocalizationProvider>
    </FormControl>
  )
}

DatePickerV2.defaultProps = {
  variant: 'standard',
  datePickerProps: {},
}

export default DatePickerV2
